import { useEffect, useState } from 'react'
import { BackButton } from '../components/BackButton'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import { useToast } from '../components/Toasts'
import { ErrorState, LoadingBlock } from '../components/States'
import { useAsync } from '../lib/useAsync'
import type { MarketSurvey, SurveyColumn, UploadJob } from '../types'

/**
 * Adding a survey. Two steps, and the second one is the one that matters.
 *
 * Step one takes the file. Step two shows what we read each column as, with a
 * real value from her own file beside it, and nothing is stored until she has
 * confirmed that. A pay column labelled as the wrong thing is invisible six
 * months later, by which time somebody has quoted it in a pay review.
 */
/**
 * The values this company has used before, for a field to suggest.
 *
 * Suggestions only, never a closed list. A compensation team's first survey
 * from a new provider must not be harder to load than its tenth from an old
 * one, and nothing here knows which providers exist in the world.
 *
 * Worth more than the saved typing: the duplicate warning compares provider
 * names, so "Radford" typed the same way every time is what lets it notice a
 * survey she already holds. Left to free typing, "Radford" and "Radford Inc"
 * become two providers and the warning goes quiet.
 */
function usedBefore(surveys: MarketSurvey[] | null, of: (s: MarketSurvey) => string): string[] {
  const seen = new Map<string, string>()
  for (const s of surveys ?? []) {
    const value = of(s).trim()
    if (!value) continue
    // First spelling wins, so the list does not show Radford twice in two cases.
    if (!seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value)
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

export function AddSurvey() {
  const nav = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const surveys = useAsync(() => api.listSurveys(), [])
  // Set when adding rows to a survey already held, which takes its provider,
  // year and industry from that survey rather than asking again.
  const addingTo = params.get('survey') ?? ''

  const [provider, setProvider] = useState('')
  const [year, setYear] = useState('')
  const [industry, setIndustry] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [job, setJob] = useState<UploadJob | null>(null)

  function choose(chosen: File | null) {
    setFileError(null)
    if (!chosen) return setFile(null)
    // Refused here, before anything is uploaded, rather than after. Excel is
    // what a provider sends, so Excel is what she will have.
    if (!/\.(csv|xlsx)$/i.test(chosen.name)) {
      setFile(null)
      setFileError(
        /\.xls$/i.test(chosen.name)
          ? 'That is the older Excel format. Open it and use Save As to make a .xlsx or a .csv.'
          : 'A survey takes a .csv or a .xlsx file, which is what a provider sends.',
      )
      return
    }
    setFile(chosen)
  }

  async function read() {
    if (!file) return setFileError('Choose the survey file first.')
    setBusy(true)
    try {
      const started = await api.startSurveyUpload({
        file,
        provider,
        year,
        industry,
        surveyId: addingTo || undefined,
      })
      setJob(started)
    } catch (err) {
      toast.fail((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (job) {
    return (
      <WaitForRead
        job={job}
        onDone={(surveyId) => nav(`/market-data?survey=${encodeURIComponent(surveyId)}`)}
        onCancel={() => setJob(null)}
      />
    )
  }

  return (
    <section className="screen screen--narrow">
      {/* The way out, in the corner, like every other screen reached from another. */}
      <BackButton to="market data" onClick={() => nav('/market-data')} />
      <header className="title-row">
        <h1 className="page">{addingTo ? 'Add rows to this survey' : 'Add a survey'}</h1>
      </header>

      <div className="card card--pad stack">
        {addingTo ? (
          <p className="hint">
            These rows join the survey you came from. Its provider, year and
            industry already apply to them, so there is nothing to type here.
          </p>
        ) : (
          <>
            {/*
              Each of these suggests what she has used before and still takes
              anything typed. A closed list would be wrong: we do not know
              every provider, and the first survey from a new one must not be
              harder to load than the tenth from an old one.
            */}
            <Suggesting
              label="Provider"
              value={provider}
              onChange={setProvider}
              placeholder="Radford, Mercer, WTW, Korn Ferry"
              options={usedBefore(surveys.data, (s) => s.provider)}
            />
            <Suggesting
              label="Year"
              value={year}
              onChange={setYear}
              placeholder="2026"
              options={usedBefore(surveys.data, (s) => s.year)}
            />
            <Suggesting
              label="Industry"
              value={industry}
              onChange={setIndustry}
              placeholder="Technology"
              options={usedBefore(surveys.data, (s) => s.industry)}
            />
            <p className="hint">
              These three apply to the whole file. Where the file has its own
              column for one of them, the file wins for that row.
              {surveys.data?.length
                ? ' Start typing to pick something you have used before.'
                : ''}
            </p>
          </>
        )}

        <div className="actions">
          <label className="file-btn">
            ↑ Choose a CSV or Excel file
            <input
              type="file"
              accept=".csv,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => {
                choose(e.target.files?.[0] ?? null)
                e.target.value = ''
              }}
            />
          </label>
          {file && <span className="hint">Chosen {file.name}</span>}
        </div>

        {fileError && <p className="field__error" role="alert">{fileError}</p>}

        {/*
          There is deliberately no paste box, no template and no sample file.
          Nobody pastes a licensed survey, and there is no format to teach,
          because whatever the provider sent is the right shape by definition.
          Anything resembling a format spec here tells people to reshape a file
          they do not need to reshape.
        */}

        <div className="form-foot">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!file || busy}
            onClick={() => void read()}
          >
            {busy ? 'Reading the file…' : 'Read the file'}
          </button>
          <button type="button" className="btn" onClick={() => nav('/market-data')}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}

/**
 * A text box that suggests what has been used before.
 *
 * A native datalist rather than a combobox of our own: it types like a text
 * box, suggests like a dropdown, is reachable by keyboard and read correctly
 * by a screen reader, and there is nothing of ours to go wrong.
 */
function Suggesting({
  label,
  value,
  onChange,
  placeholder,
  options,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  placeholder: string
  options: string[]
}) {
  const id = `suggest-${label.toLowerCase()}`
  return (
    <label className="field">
      <span>{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        list={options.length ? id : undefined}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
      {options.length > 0 && (
        <datalist id={id}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
    </label>
  )
}

/**
 * Waits for the file to be read.
 *
 * **The upload finishing is not the file being read.** Putting the file in
 * storage is what starts the reading, which happens in a Lambda and takes a
 * model call and a pass over every row. The job comes back the moment the file
 * lands, with no columns on it yet.
 *
 * This was found on the deployed system and not before, because the offline
 * stand-in reads the file the instant it is handed over. A stand-in that is
 * faster than the real thing hides a wait exactly as effectively as one that
 * is kinder hides a refusal, and the screen was written against timing that
 * only ever existed in the browser.
 */
function WaitForRead({
  job,
  onDone,
  onCancel,
}: {
  job: UploadJob
  onDone: (surveyId: string) => void
  onCancel: () => void
}) {
  const [current, setCurrent] = useState(job)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    if (current.status === 'validated' || current.status === 'failed') return
    let live = true
    const timer = window.setInterval(async () => {
      try {
        const next = await api.getUpload(job.id)
        if (!live) return
        // The duplicate warning was worked out at presign and is not on the
        // job that comes back, so it is carried across rather than lost.
        setCurrent({ ...next, possibleDuplicates: job.possibleDuplicates })
        if (next.status === 'failed') setFailed(next.error ?? 'That file could not be read.')
      } catch (err) {
        if (live) setFailed((err as Error).message)
      }
    }, 1500)
    return () => {
      live = false
      window.clearInterval(timer)
    }
  }, [current.status, job.id, job.possibleDuplicates])

  if (failed) return <ErrorState message={failed} onRetry={onCancel} />

  if (current.status !== 'validated') {
    return (
      <section className="screen screen--narrow">
        <LoadingBlock label="Reading the file" />
        <p className="hint">
          {current.market?.rowsRead
            ? `${current.market.rowsRead.toLocaleString()} rows read so far.`
            : 'Working out what each column is.'}{' '}
          A large survey takes a few minutes. You can leave this page and come
          back to it.
        </p>
      </section>
    )
  }

  return <ConfirmColumns job={current} onDone={onDone} onCancel={onCancel} />
}

/**
 * Step two. What we read each column as, and the two things worth pointing out.
 */
function ConfirmColumns({
  job,
  onDone,
  onCancel,
}: {
  job: UploadJob
  onDone: (surveyId: string) => void
  onCancel: () => void
}) {
  const toast = useToast()
  const detail = job.market
  const [columns, setColumns] = useState<SurveyColumn[]>(detail?.columns ?? [])
  // Unticked to start with. A thin row loads unless she says otherwise.
  const [dropThin, setDropThin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  if (!detail) return <ErrorState message="That upload has nothing to confirm." />
  if (busy) return <LoadingBlock label="Storing the rows" />

  const duplicates = job.possibleDuplicates ?? []
  const unreadable = detail.unreadable
  const thin = detail.thin

  async function store() {
    setBusy(true)
    setFailed(null)
    try {
      await api.confirmColumns(job.id, columns)
      await api.commitSurvey(job.id, dropThin)
      toast.ok('Survey stored.')
      const after = await api.getUpload(job.id)
      onDone(after.market?.surveyId ?? '')
    } catch (err) {
      setFailed((err as Error).message)
      setBusy(false)
    }
  }

  const willStore = (detail.rowsRead ?? 0) - (dropThin ? thin?.count ?? 0 : 0)

  return (
    <section className="screen">
      <header className="title-row">
        <div>
          <h1 className="page">Check the column names</h1>
          <p className="subline">
            {job.fileName} · {(detail.rowsRead ?? 0).toLocaleString()} rows read
          </p>
        </div>
      </header>

      {/*
        She may already hold this. A warning and never a refusal: two cuts from
        one provider in one year is a normal thing to own, so carrying on is an
        ordinary button rather than a buried one.
      */}
      {duplicates.length> 0 && (
        <div className="banner banner--block" role="status">
          <h2>You may already have this</h2>
          <ul>
            {duplicates.map((d) => (
              <li key={d.id + d.fileName}>
                <b>{d.provider} {d.year}</b>
                {d.fileName ? ` · ${d.fileName}` : ''} · {d.rowCount.toLocaleString()} records ·
                loaded {new Date(d.uploadedAt).toLocaleDateString()}
                <span className="subline"> (matched on {d.matchedOn})</span>
              </li>
            ))}
          </ul>
          <p>
            If this is a different cut, carry on. If a colleague loaded it last
            week, cancel and nothing is stored.
          </p>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Their heading</th>
              <th>What we read it as</th>
              <th>A value from your file</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((col, i) => (
              <tr key={col.key}>
                {/* The provider's own heading, never editable. */}
                <td><code>{col.heading}</code></td>
                <td>
                  {/*
                    The correction this whole screen exists for. The model is
                    good at reading a heading and will still be wrong
                    sometimes, and a pay column labelled as the wrong thing is
                    invisible six months later.
                  */}
                  <input
                    className="col-name"
                    value={col.name}
                    aria-label={`What ${col.heading} is`}
                    onChange={(e) =>
                      setColumns((prev) =>
                        prev.map((c, j) => (j === i ? { ...c, name: e.target.value } : c)),
                      )
                    }
                  />
                  <select
                    className="col-kind"
                    value={col.kind}
                    aria-label={`What sort of thing ${col.heading} holds`}
                    onChange={(e) =>
                      setColumns((prev) =>
                        prev.map((c, j) =>
                          j === i ? { ...c, kind: e.target.value as SurveyColumn['kind'] } : c,
                        ),
                      )
                    }
                  >
                    <option value="text">Text</option>
                    <option value="number">A number</option>
                    <option value="money">Money</option>
                  </select>
                  {col.currency && <span className="subline"> {col.currency}</span>}
                </td>
                {/*
                  A real value from her own file. This is what makes twenty two
                  rows checkable in seconds rather than guesswork: she runs her
                  eye down this column and stops only where something looks
                  wrong.
                */}
                <td className="subline">{detail.samples?.[col.key] ?? <em>blank</em>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail.newColumns?.length ? (
        <div className="banner banner--block" role="status">
          <h2>New columns for this survey</h2>
          <p>
            {detail.newColumns.join(', ')} {detail.newColumns.length === 1 ? 'is' : 'are'} not in
            the survey already. {detail.newColumns.length === 1 ? 'It' : 'They'} will be added, and
            the rows already stored simply will not have {detail.newColumns.length === 1 ? 'it' : 'them'}.
          </p>
        </div>
      ) : null}

      {unreadable && unreadable.count> 0 && (
        <div className="banner banner--warn banner--block" role="status">
          <h2>{unreadable.count.toLocaleString()} rows could not be read</h2>
          <p>The rest of the file still loads. These rows had the wrong number of cells:</p>
          <ul>
            {unreadable.shown.map((r) => (
              <li key={r.line}>Row {r.line} {r.reason}</li>
            ))}
          </ul>
          {unreadable.count> unreadable.shown.length && (
            <p className="subline">
              Showing the first {unreadable.shown.length} of {unreadable.count.toLocaleString()}.
            </p>
          )}
        </div>
      )}

      {thin && thin.count> 0 && (
        <div className="banner banner--block" role="status">
          <h2>{thin.count.toLocaleString()} rows have far fewer cells filled than the rest</h2>
          {/*
            Worded as information, not an error. A provider withholds a figure
            where too few companies reported it, so a thin row is usually them
            saying there is not enough there to publish. She has done nothing
            wrong and should not go hunting for a mistake that is not there.
          */}
          <p>
            That is usually the provider holding a figure back because too few
            companies reported it, rather than anything wrong with your file.
            They load unless you say otherwise.
          </p>
          <label className="field field--check">
            <input
              type="checkbox"
              checked={dropThin}
              onChange={(e) => setDropThin(e.target.checked)}
            />
            <span>Leave these {thin.count.toLocaleString()} rows out</span>
          </label>
        </div>
      )}

      {failed && <ErrorState message={failed} onRetry={() => void store()} />}

      <div className="form-foot">
        <button type="button" className="btn btn--primary" onClick={() => void store()}>
          Store {willStore.toLocaleString()} rows
        </button>
        <button type="button" className="btn" onClick={onCancel}>Back</button>
      </div>
    </section>
  )
}
